import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';

interface CommitNumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'min' | 'max'> {
  value: number;
  /** Called with the parsed, clamped value on blur or Enter (only when it changed). */
  onCommit: (value: number) => void;
  min: number;
  max: number;
  /** Decimal places shown and kept; 0 rounds to whole numbers. */
  decimals?: number;
}

/**
 * Number field that lets people type freely (including clearing it) and only applies the value on
 * blur or Enter, clamped to [min, max]. Escape or an unparseable entry reverts to the current value.
 * A plain controlled number input re-formats on every keystroke, which makes it impossible to clear.
 */
export function CommitNumberInput({
  value,
  onCommit,
  min,
  max,
  decimals = 0,
  onBlur,
  onKeyDown,
  onFocus,
  ...rest
}: CommitNumberInputProps) {
  const format = (n: number) => (decimals > 0 ? n.toFixed(decimals) : String(Math.round(n)));
  const [draft, setDraft] = useState(() => format(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(format(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decimals]);

  const commit = () => {
    const parsed = parseFloat(draft);
    if (!Number.isFinite(parsed)) {
      setDraft(format(value));
      return;
    }
    const factor = 10 ** decimals;
    const clamped = Math.min(max, Math.max(min, Math.round(parsed * factor) / factor));
    setDraft(format(clamped));
    if (clamped !== value) {
      onCommit(clamped);
    }
  };

  return (
    <Input
      {...rest}
      type="number"
      inputMode={decimals > 0 ? 'decimal' : 'numeric'}
      min={min}
      max={max}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => {
        focusedRef.current = true;
        onFocus?.(e);
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        commit();
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setDraft(format(value));
          (e.target as HTMLInputElement).blur();
        }
        onKeyDown?.(e);
      }}
    />
  );
}
